import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook for fetching match visualization metadata only
 * Returns stat type IDs, tags, and data existence check without fetching full events
 * This is optimized to reduce egress by fetching only minimal data needed for filters
 */
export const useMatchVisualizationMetadata = (matchId: string | undefined) => {
  const [statTypeIds, setStatTypeIds] = useState<Set<string>>(new Set());
  const [tags, setTags] = useState<string[]>([]);
  const [hasData, setHasData] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetadata = useCallback(async () => {
    if (!matchId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch only stat_type_id and tags columns (no position_data or other heavy fields)
      const { data, error: fetchError } = await supabase
        .from('match_visualization')
        .select('stat_type_id, tags')
        .eq('match_id', matchId)
        .eq('is_active', true);

      if (fetchError) {
        console.error('Error fetching visualization metadata:', fetchError);
        setError('Failed to fetch visualization metadata');
        return;
      }

      // Extract unique stat type IDs
      const uniqueStatTypeIds = new Set<string>();
      const tagSet = new Set<string>();

      (data || []).forEach((event: any) => {
        // Collect stat type IDs
        if (event.stat_type_id) {
          uniqueStatTypeIds.add(event.stat_type_id);
        }

        // Extract tags from JSON
        if (event.tags) {
          try {
            const parsedTags = typeof event.tags === 'string' 
              ? JSON.parse(event.tags) 
              : event.tags;
            
            if (Array.isArray(parsedTags)) {
              parsedTags.forEach((tag: string) => {
                if (tag && typeof tag === 'string') {
                  tagSet.add(tag);
                }
              });
            }
          } catch (e) {
            console.warn('Failed to parse tags for metadata:', e);
          }
        }
      });

      setStatTypeIds(uniqueStatTypeIds);
      setTags(Array.from(tagSet).sort());
      setHasData((data || []).length > 0);
    } catch (err) {
      console.error('Error in useMatchVisualizationMetadata:', err);
      setError('Error fetching visualization metadata');
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  return {
    statTypeIds,
    tags,
    hasData,
    loading,
    error,
    refetch: fetchMetadata,
  };
};

